import React, { useState, useEffect, useRef, useMemo } from 'react';
import { YANMAR_CURVE, TRIP_TYPES, DEST_TYPES, BARO_TREND, VISIBILITY_OPTS, isStay } from '../lib/constants';
import { sailingPct, movingMotorHrs, nightsBetween } from '../lib/utils';
import { uploadPhoto, deletePhoto } from '../lib/db';

// ── Orca screenshot time helpers ──
const orcaDurH = (s) => { if (!s) return null; const h=/(\d+)\s*h/.exec(s), m=/(\d+)\s*min/.exec(s); const H=(h?+h[1]:0)+(m?+m[1]:0)/60; return H>0?Math.round(H*100)/100:null; };
const orcaHHMM = (h) => { const H=Math.floor(h), M=Math.round((h-H)*60); return `${String(H).padStart(2,'0')}:${String(M).padStart(2,'0')}`; };
const orcaNormTime = (s) => { const m=/(\d{1,2}):(\d{2})/.exec(s||''); return m?`${String(+m[1]).padStart(2,'0')}:${m[2]}`:(s||''); };
const orcaAddTime = (hhmm, addH) => { const m=/(\d{1,2}):(\d{2})/.exec(hhmm||''); if (!m) return ''; let t=(+m[1])*60+(+m[2])+Math.round(addH*60); t=((t%1440)+1440)%1440; return `${String(Math.floor(t/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`; };

// ── Historical weather + sea state at a point/time (Open-Meteo, no API key) ──
const DEG_TO_COMPASS = (d) => ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'][Math.round(d / 22.5) % 16];

async function fetchConditions(lat, lon, dateISO, hhmm) {
  if (lat == null || lon == null || !dateISO) return null;
  const hour = Math.min(23, Math.max(0, parseInt((hhmm || '12:00').split(':')[0], 10) || 12));
  const pick = (arr, times) => {
    if (!arr || !times) return null;
    const i = times.findIndex(t => new Date(t).getHours() === hour);
    const j = i >= 0 ? i : 0;
    return arr[j] == null ? null : arr[j];
  };
  const out = {};
  try {
    const w = await fetch(`https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${dateISO}&end_date=${dateISO}&hourly=temperature_2m,pressure_msl,wind_speed_10m,wind_direction_10m,cloud_cover,visibility&wind_speed_unit=kn&temperature_unit=fahrenheit&timezone=auto`);
    if (w.ok) {
      const d = await w.json();
      const h = d.hourly || {};
      const spd = pick(h.wind_speed_10m, h.time);
      const dir = pick(h.wind_direction_10m, h.time);
      const baro = pick(h.pressure_msl, h.time);
      const temp = pick(h.temperature_2m, h.time);
      const cloud = pick(h.cloud_cover, h.time);
      const vis = pick(h.visibility, h.time);
      if (spd != null) out.wind_speed = Math.round(spd);
      if (dir != null) out.wind_dir = `${DEG_TO_COMPASS(dir)} · ${Math.round(dir)}°`;
      if (baro != null) out.baro = Math.round(baro);
      if (temp != null) out.temp = Math.round(temp);
      if (cloud != null) out.conditions = cloud < 20 ? 'Clear' : cloud < 55 ? 'Partly cloudy' : cloud < 85 ? 'Mostly cloudy' : 'Overcast';
      if (vis != null) { const nm = vis / 1852; out.visibility = nm >= 6 ? 'Good' : nm >= 2 ? 'Moderate' : nm >= 1 ? 'Poor' : 'Fog'; }
    }
  } catch { /* offline or blocked: leave blank */ }
  try {
    const m = await fetch(`https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lon}&start_date=${dateISO}&end_date=${dateISO}&hourly=wave_height,wave_direction&length_unit=imperial&timezone=auto`);
    if (m.ok) {
      const d = await m.json();
      const h = d.hourly || {};
      const wh = pick(h.wave_height, h.time);
      const wd = pick(h.wave_direction, h.time);
      if (wh != null && wh > 0) out.waves = `${wh.toFixed(1)}ft${wd != null ? ` ${DEG_TO_COMPASS(wd)}` : ''}`;
    }
  } catch { /* marine data unavailable inland: skip */ }
  return Object.keys(out).length ? out : null;
}

// ── Reverse geocode a GPS point to a nearby place name (OpenStreetMap Nominatim) ──
async function reverseGeocode(lat, lon) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=13&addressdetails=1`, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) return null;
    const d = await r.json();
    const a = d.address || {};
    return a.harbour || a.bay || a.marina || a.hamlet || a.village || a.town || a.suburb || a.neighbourhood || a.city || a.locality || a.county || null;
  } catch { return null; }
}

const FUEL_LEVELS = [{v:0,l:'E'},{v:.25,l:'¼'},{v:.5,l:'½'},{v:.75,l:'¾'},{v:1,l:'F'}];

function Lbl({ children, sub }) {
  return <div style={{ marginBottom:4, minHeight:25 }}><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--slate)' }}>{children}</div>{sub&&<div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', opacity:.7, marginTop:1 }}>{sub}</div>}</div>;
}

function Sep({ children, first }) {
  return <div style={{ gridColumn:'1/-1', borderTop:first?'none':'1px solid var(--border)', paddingTop:first?0:14, marginTop:first?0:6, fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em', color:'var(--gold)' }}>{children}</div>;
}

function Check({ label, checked, onChange, children }) {
  return <div><label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'6px 0' }}><input type="checkbox" checked={!!checked} onChange={e=>onChange(e.target.checked)} style={{ width:16, height:16, accentColor:'var(--navy)', cursor:'pointer', flexShrink:0 }}/><span style={{ fontSize:13, color:'var(--ink-s)' }}>{label}</span></label>{checked&&children&&<div style={{ marginLeft:24, marginTop:4 }}>{children}</div>}</div>;
}

function HrsRow({ label, sKey, eKey, sVal, eVal, onChange }) {
  const delta = sVal && eVal && Number(eVal) > Number(sVal) ? (Number(eVal)-Number(sVal)).toFixed(1) : null;
  return (
    <div style={{ gridColumn:'1/-1' }}>
      <Lbl>{label}</Lbl>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 70px', gap:6 }}>
        <div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2 }}>START</div><input type="number" step=".1" value={sVal||''} onChange={e=>onChange(sKey,e.target.value)} style={{ borderColor:'rgba(184,134,11,.4)', fontFamily:"'IBM Plex Mono',monospace" }}/></div>
        <div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2 }}>END ★</div><input type="number" step=".1" value={eVal||''} onChange={e=>onChange(eKey,e.target.value)} placeholder="on arrival" style={{ borderColor:'var(--gold)', fontFamily:"'IBM Plex Mono',monospace" }}/></div>
        <div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2 }}>RUN</div><div style={{ background:'var(--s2)', border:'1px solid var(--border)', borderRadius:7, padding:'8px 10px', fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:600, color:delta?'var(--green)':'var(--slate)', height:36, display:'flex', alignItems:'center' }}>{delta?`${delta}h`:'—'}</div></div>
      </div>
    </div>
  );
}

function TankRow({ label, sKey, eKey, sVal, eVal, onChange }) {
  const Picker = ({value, onPick, accent}) => (
    <div style={{ display:'flex', gap:3 }}>
      {FUEL_LEVELS.map(lv => (
        <button key={lv.v} type="button" onClick={()=>onPick(lv.v)} style={{ flex:1, padding:'7px 0', borderRadius:6, border:`1px solid ${Number(value)===lv.v?accent:'var(--border)'}`, background:Number(value)===lv.v?accent:'var(--surface)', color:Number(value)===lv.v?'white':'var(--slate)', fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .15s' }}>{lv.l}</button>
      ))}
    </div>
  );
  const delta = sVal!==undefined && eVal!==undefined && sVal!=='' && eVal!=='' ? Number(sVal)-Number(eVal) : null;
  return (
    <div style={{ gridColumn:'1/-1' }}>
      <Lbl>{label}</Lbl>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:3 }}>START</div><Picker value={sVal} onPick={v=>onChange(sKey,v)} accent="rgba(184,134,11,.85)"/></div>
        <div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:3 }}>END ★</div><Picker value={eVal} onPick={v=>onChange(eKey,v)} accent="var(--gold)"/></div>
      </div>
      {delta!==null&&delta!==0&&<div style={{ marginTop:5, fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:delta>0?'var(--clay)':'var(--green)' }}>{delta>0?`↓ used ${(delta*100).toFixed(0)}%`:`↑ refilled ${(Math.abs(delta)*100).toFixed(0)}%`}</div>}
    </div>
  );
}

function GPXUpload({ onParsed, existing }) {
  const ref = useRef();
  const [name, setName] = useState(null);
  const [result, setResult] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const alreadyHas = existing && !name && !result;

  const handleFile = (file) => {
    setName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const doc = new DOMParser().parseFromString(e.target.result, 'application/xml');

        // ── UTC → Pacific local time ──────────────────────────
        // Pacific: UTC-8 standard, UTC-7 daylight (Mar–Nov)
        const utcToPacific = (utcDate) => {
          const month = utcDate.getUTCMonth(); // 0-based
          const isDST = month >= 2 && month <= 10; // Mar-Nov approx
          const offsetMs = isDST ? 7*3600000 : 8*3600000;
          return new Date(utcDate.getTime() - offsetMs);
        };

        const fmtLocal = (utcDate) => {
          const local = utcToPacific(utcDate);
          return `${String(local.getUTCHours()).padStart(2,'0')}:${String(local.getUTCMinutes()).padStart(2,'0')}`;
        };

        const fmtLocalDate = (utcDate) => {
          const local = utcToPacific(utcDate);
          return local.toISOString().slice(0,10);
        };

        // ── Orca track name → auto-fill title ────────────────
        // Format: "Logbook-Pardon My French-15 Jun 2026"
        const trackName = doc.querySelector('trk name')?.textContent?.trim() || '';
        let autoTitle = null;
        const orcaMatch = trackName.match(/^Logbook-(.+?)-\d{1,2}\s+\w+\s+\d{4}$/);
        if (orcaMatch) autoTitle = null; // boat name not useful as title — skip
        else if (trackName && !trackName.startsWith('Logbook-')) autoTitle = trackName;

        // ── Parse track points ────────────────────────────────
        const pts = Array.from(doc.querySelectorAll('trkpt')).map(pt => ({
          lat: parseFloat(pt.getAttribute('lat')),
          lon: parseFloat(pt.getAttribute('lon')),
          time: new Date(pt.querySelector('time')?.textContent || 0),
          spd: parseFloat(pt.querySelector('speed')?.textContent || 0) * 1.94384,
        })).filter(p => !isNaN(p.lat) && p.time.getFullYear() > 2000)
           .sort((a,b) => a.time - b.time);

        if (!pts.length) return;

        // ── Calculate speed from coords where not embedded ────
        for (let i = 0; i < pts.length; i++) {
          if (!pts[i].spd && i > 0) {
            const dt = (pts[i].time - pts[i-1].time) / 3600000;
            const dLat = (pts[i].lat - pts[i-1].lat) * Math.PI/180;
            const dLon = (pts[i].lon - pts[i-1].lon) * Math.PI/180;
            const x = Math.sin(dLat/2)**2 + Math.cos(pts[i-1].lat*Math.PI/180)*Math.cos(pts[i].lat*Math.PI/180)*Math.sin(dLon/2)**2;
            pts[i].spd = dt > 0 ? 3440.065*2*Math.atan2(Math.sqrt(x), Math.sqrt(1-x)) / dt : 0;
          }
        }

        // ── Distance, moving time, speed stats ───────────────
        let nm=0, movingHrs=0, maxSog=0, movingSogSum=0, movingSogN=0;
        for (let i = 1; i < pts.length; i++) {
          const dLat = (pts[i].lat-pts[i-1].lat)*Math.PI/180;
          const dLon = (pts[i].lon-pts[i-1].lon)*Math.PI/180;
          const x = Math.sin(dLat/2)**2 + Math.cos(pts[i-1].lat*Math.PI/180)*Math.cos(pts[i].lat*Math.PI/180)*Math.sin(dLon/2)**2;
          const d = 3440.065*2*Math.atan2(Math.sqrt(x), Math.sqrt(1-x));
          if (d < 50) nm += d;
          const dt = (pts[i].time - pts[i-1].time) / 3600000;
          if (pts[i].spd > 0.3 && dt < 2) {
            movingHrs += dt;
            movingSogSum += pts[i].spd;
            movingSogN++;
          }
          if (pts[i].spd > maxSog) maxSog = pts[i].spd;
        }

        // ── Departure/arrival = first/last SUSTAINED moving point ──
        // Orca records continuously at the dock and at anchor; a lone >0.3kt
        // blip there is GPS jitter on the mooring lines. Require speed held
        // above ~1kt across a short window to catch the real leaving/arriving.
        const sustained = (i, dir) => {
          let seen = 0, moving = 0;
          for (let k = 1; k <= 6; k++) {
            const j = i + dir * k;
            if (j < 0 || j >= pts.length) break;
            seen++; if (pts[j].spd > 1.0) moving++;
          }
          return seen >= 4 && moving >= 4;
        };
        let firstMove = pts[0], lastMove = pts[pts.length - 1];
        for (let i = 0; i < pts.length; i++)     if (pts[i].spd > 1.0 && sustained(i,  1)) { firstMove = pts[i]; break; }
        for (let i = pts.length - 1; i >= 0; i--) if (pts[i].spd > 1.0 && sustained(i, -1)) { lastMove  = pts[i]; break; }

        // ── Downsampled track (~1 point/min, ≤400 pts) for the route map ──
        const fi = pts.indexOf(firstMove), li = pts.indexOf(lastMove);
        const voyage = pts.slice(Math.max(0, fi), (li >= 0 ? li : pts.length - 1) + 1);
        const step = Math.max(1, Math.ceil(voyage.length / 400));
        const track = voyage
          .filter((_, i) => i % step === 0 || i === voyage.length - 1)
          .map(p => ({
            lat: Math.round(p.lat * 100000) / 100000,
            lon: Math.round(p.lon * 100000) / 100000,
            t:   p.time.toISOString(),
            spd: Math.round(p.spd * 10) / 10,
          }));

        // ── Trip type guess ───────────────────────────────────
        const tripType = movingHrs > 10 ? 'Passage' : nm < 1 ? 'At Anchor' : 'Day Sail';

        const r = {
          date:        fmtLocalDate(firstMove.time),
          depart_time: fmtLocal(firstMove.time),
          arrive_time: fmtLocal(lastMove.time),
          nm:          Math.round(nm*10)/10,
          moving_hrs:  Math.round(movingHrs*100)/100,
          max_sog:     Math.round(maxSog*10)/10,
          avg_sog:     movingSogN ? Math.round((movingSogSum/movingSogN)*10)/10 : 0,
          start_lat:   Math.round(firstMove.lat*10000)/10000,
          start_lon:   Math.round(firstMove.lon*10000)/10000,
          end_lat:     Math.round(lastMove.lat*10000)/10000,
          end_lon:     Math.round(lastMove.lon*10000)/10000,
          track,
          trip_type:   tripType,
          title:       autoTitle,
        };
        setResult(r);
        onParsed(r);
      } catch(err) { console.error('GPX parse error', err); }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      {alreadyHas ? (
        <div style={{ display:'flex', alignItems:'center', gap:12, border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', background:'var(--tint, #FAF8F4)' }}>
          <span style={{ flex:'0 0 34px', width:34, height:34, borderRadius:9, background:'rgba(26,107,46,.1)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--green)' }}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </span>
          <span style={{ minWidth:0, flex:1 }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, color:'var(--navy)' }}>GPS track imported</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', marginTop:2 }}>{existing.points} points · {existing.nm != null ? `${existing.nm} nm` : 'route saved'}</div>
          </span>
          <button onClick={()=>{ setReplacing(true); ref.current.click(); }} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:600, color:'var(--gold)', background:'none', border:'none', cursor:'pointer' }}>Replace</button>
          <input ref={ref} type="file" accept=".gpx" style={{ display:'none' }} onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
        </div>
      ) : (
        <div onClick={()=>!name&&ref.current.click()} style={{ border:`2px dashed ${result?'var(--green)':name?'var(--gold)':'var(--border)'}`, borderRadius:10, padding:'13px 16px', textAlign:'center', cursor:name?'default':'pointer', background:result?'rgba(26,107,46,.05)':name?'rgba(184,134,11,.05)':'transparent', position:'relative' }}>
          <input ref={ref} type="file" accept=".gpx" style={{ display:'none' }} onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
          {name&&<button onClick={e=>{e.stopPropagation();setName(null);setResult(null);ref.current.value='';}} style={{ position:'absolute', top:6, right:8, background:'none', border:'none', fontSize:14, color:'var(--slate)', cursor:'pointer' }}>✕</button>}
          <div style={{ fontSize:13, fontWeight:600, color:result?'var(--green)':name?'var(--gold)':'var(--slate)' }}>{name || (replacing ? 'Replace GPX track' : 'Upload GPX track')}</div>
          <div style={{ fontSize:11, color:'var(--slate)', marginTop:2 }}>{result?`✓ ${result.nm}nm · ${result.depart_time}→${result.arrive_time} · ${result.moving_hrs}h moving`:name?'Parsing…':'.gpx from Orca, Garmin, or any GPS'}</div>
        </div>
      )}
    </div>
  );
}

// ── Orca Screenshot Upload ────────────────────────────────────
// ── Photo upload (Supabase Storage) ──────────────────────────────
function PhotoUpload({ photos = [], coverUrl, date, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(0);
  const [err, setErr] = useState('');
  const list = Array.isArray(photos) ? photos : [];

  const handleFiles = async (files) => {
    setErr('');
    const arr = [...files];
    setBusy(arr.length);
    const added = [];
    for (const f of arr) {
      try { added.push(await uploadPhoto(f, date)); }
      catch (e) { setErr(e.message || 'Upload failed'); }
      setBusy(b => b - 1);
    }
    if (added.length) {
      const next = [...list, ...added];
      onChange(next, coverUrl || next[0].url);
    }
    setBusy(0);
  };

  const remove = async (p) => {
    const next = list.filter(x => x.url !== p.url);
    onChange(next, coverUrl === p.url ? (next[0]?.url || null) : coverUrl);
    try { await deletePhoto(p.path); } catch { /* keep the entry consistent even if storage delete fails */ }
  };

  const box = { aspectRatio:'1', borderRadius:10, overflow:'hidden', border:'1px solid var(--border)' };
  return (
    <div>
      <Lbl>Photos{list.length ? ` · ${list.length}` : ''}</Lbl>
      {list.length === 0 ? (
        <div onClick={()=>ref.current.click()} style={{ border:'1.5px dashed var(--border)', borderRadius:12, background:'var(--surface)', padding:'24px 16px', textAlign:'center', cursor:'pointer' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--navy)' }}>{busy ? 'Uploading…' : 'Add photos'}</div>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:3 }}>Tap to choose from your library</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {list.map(p => (
            <div key={p.url} style={{ ...box, position:'relative' }}>
              <img src={p.url} alt="" loading="lazy" decoding="async" onClick={()=>onChange(list, p.url)} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', cursor:'pointer' }}/>
              {coverUrl === p.url && <span style={{ position:'absolute', left:4, bottom:4, fontFamily:"'IBM Plex Mono',monospace", fontSize:7, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--navy)', background:'var(--gold-lt)', padding:'2px 5px', borderRadius:4 }}>Cover</span>}
              <button type="button" onClick={()=>remove(p)} style={{ position:'absolute', top:4, right:4, width:20, height:20, borderRadius:'50%', background:'rgba(11,22,38,.75)', color:'#fff', border:'none', fontSize:12, cursor:'pointer', lineHeight:1 }}>×</button>
            </div>
          ))}
          <div onClick={()=>ref.current.click()} style={{ ...box, border:'1.5px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold-deep, #8B6508)', fontSize:24, background:'var(--surface)', cursor:'pointer' }}>{busy ? '…' : '+'}</div>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={e=>e.target.files.length && handleFiles(e.target.files)}/>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color: err ? 'var(--red)' : 'var(--slate)', marginTop:7, lineHeight:1.5 }}>
        {err || (list.length ? 'Tap a photo to make it the cover.' : 'The cover photo shows in the log feed and report.')}
      </div>
    </div>
  );
}

// ── Multi-screenshot Orca upload ──────────────────────────────
function MultiOrcaUpload({ onParsed, existing }) {
  const ref = useRef();
  const [slots, setSlots] = useState([]);
  const [mergedResult, setMergedResult] = useState(null);
  const alreadyHas = existing && slots.length === 0 && !mergedResult;

  const parseFile = async (file) => {
    const base64 = await new Promise((res,rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result.split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });
    const resp = await fetch('/.netlify/functions/parse-orca', {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ image_base64:base64, media_type:file.type||'image/png' }),
    });
    const data = await resp.json();
    if (!resp.ok || data.error) throw new Error(data.error||'Parse failed');
    return data;
  };

  const handleFiles = async (files) => {
    const newSlots = Array.from(files).map(f => ({ id:Date.now()+Math.random(), name:f.name, file:f, status:'loading', result:null }));
    setSlots(prev => [...prev, ...newSlots]);

    for (const slot of newSlots) {
      try {
        const result = await parseFile(slot.file);
        setSlots(prev => prev.map(s => s.id===slot.id ? {...s, status:'done', result} : s));
        // Merge into parent form — prefer non-null, first screenshot wins for most fields
        onParsed(result);
        setMergedResult(r => {
          const merged = { ...result, ...(r||{}) };
          // For max values, take the maximum across screenshots
          if (r?.max_sog && result.max_sog) merged.max_sog = Math.max(r.max_sog, result.max_sog);
          if (r?.max_tws && result.max_tws) merged.max_tws = Math.max(r.max_tws, result.max_tws);
          return merged;
        });
      } catch(e) {
        setSlots(prev => prev.map(s => s.id===slot.id ? {...s, status:'error', error:e.message} : s));
      }
    }
  };

  const FIELD_LABELS = [
    ['max_sog','Max SOG','kt'],['avg_sog','Avg SOG','kt'],
    ['max_tws','Max TWS','kt'],['avg_tws','Avg TWS','kt'],
    ['wind_direction','Wind Dir',''],['distance_nm','Distance','nm'],
    ['depth','Depth',''],['water_temp','Water Temp',''],
    ['avg_polar','Avg Polar','%'],['max_polar','Max Polar','%'],
  ];

  const statusIcon = s => s==='loading'?'⟳':s==='done'?'✓':'✕';
  const statusColor = s => s==='loading'?'var(--gold)':s==='done'?'var(--green)':'var(--red)';

  return (
    <div>
      {alreadyHas && (
        <div style={{ display:'flex', alignItems:'center', gap:12, border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', background:'var(--tint, #FAF8F4)', marginBottom:8 }}>
          <span style={{ flex:'0 0 34px', width:34, height:34, borderRadius:9, background:'rgba(26,107,46,.1)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--green)' }}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </span>
          <span style={{ minWidth:0, flex:1 }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, color:'var(--navy)' }}>Orca data imported</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', marginTop:2 }}>{existing.summary || 'trip stats saved'}</div>
          </span>
          <button onClick={()=>ref.current.click()} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:600, color:'var(--gold)', background:'none', border:'none', cursor:'pointer' }}>Replace</button>
          <input ref={ref} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={e=>e.target.files.length&&handleFiles(e.target.files)}/>
        </div>
      )}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
        {slots.map(slot => (
          <div key={slot.id} style={{ display:'flex', alignItems:'center', gap:5, background:'var(--s2)', border:`1px solid ${statusColor(slot.status)}30`, borderRadius:7, padding:'5px 10px' }}>
            <span style={{ color:statusColor(slot.status), fontSize:12, fontWeight:700 }}>{statusIcon(slot.status)}</span>
            <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{slot.name}</span>
            <button onClick={()=>setSlots(prev=>prev.filter(s=>s.id!==slot.id))} style={{ background:'none', border:'none', color:'var(--slate)', cursor:'pointer', fontSize:12, lineHeight:1, padding:0 }}>✕</button>
          </div>
        ))}
        <button onClick={()=>ref.current.click()} style={{ padding:'5px 12px', borderRadius:7, border:'1.5px dashed var(--border)', background:'transparent', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--slate)', cursor:'pointer' }}>
          📱 {slots.length===0?'Add screenshot':'Add another'}
        </button>
        <input ref={ref} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={e=>e.target.files.length&&handleFiles(e.target.files)}/>
      </div>

      {mergedResult && (
        <div style={{ background:'rgba(26,107,46,.06)', border:'1px solid rgba(26,107,46,.2)', borderRadius:8, padding:'10px 12px' }}>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--green)', marginBottom:6 }}>Merged from {slots.filter(s=>s.status==='done').length} screenshot{slots.filter(s=>s.status==='done').length!==1?'s':''}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:3 }}>
            {FIELD_LABELS.filter(([k]) => mergedResult[k]!=null).map(([k,l,u]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', textTransform:'uppercase', letterSpacing:'.06em' }}>{l}</span>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color:'var(--navy)' }}>{mergedResult[k]}{u}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 760);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 760);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

export default function EntryModal({ isOpen, onClose, onSave, prevEntry, carryReadings, entries = [], editEntry, defaultCaptain='Frederick', crewList=[], homeLat=47.6234, homeLon=-122.3927, engineBaseline=2940, genBaseline=1130 }) {
  const [form, setForm]     = useState({});
  const [wxS, setWxS]       = useState({});
  const [wxE, setWxE]       = useState({});
  const [saving, setSaving] = useState(false);
  const [fuelEst, setFuelEst] = useState(null);
  const [step, setStep]     = useState(0);
  const [active, setActive] = useState('import');
  const mobile    = useIsMobile();
  const secRefs   = useRef({});
  const scrollRef = useRef(null);

  // A stay sits between two passages. Its arrival time is when the previous
  // entry arrived; its departure time is when the next entry departs. Find
  // those neighbours by date (ignoring the entry being edited). Declared here,
  // above any early return, so the hook order stays stable.
  const stayNeighbours = useMemo(() => {
    const others = (entries || []).filter(e => e.id !== editEntry?.id && e.date);
    const sorted = [...others].sort((a, b) => new Date(a.date) - new Date(b.date));
    const ref = form.date;
    let prev = null, next = null;
    if (ref) {
      for (const e of sorted) {
        if (e.date <= ref) prev = e;
        else if (!next) next = e;
      }
    }
    return { prev, next };
  }, [entries, editEntry, form.date]);

  // A stay hides the four underway-only tabs; keep the current tab valid when
  // the section list shrinks (sail → stay).
  useEffect(() => {
    const hidden = isStay(form.trip_type) ? ['import','time','sails','perf'] : [];
    const visibleCount = 13 - hidden.length;
    if (step > visibleCount - 1) setStep(visibleCount - 1);
    if (hidden.includes(active)) setActive('trip');
  }, [form.trip_type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    if (editEntry) {
      // Pre-fill all fields from existing entry
      setForm({ ...editEntry });
      // Restore weather blocks from wind_start/end strings
      const ws = editEntry.wind_start || '';
      const we = editEntry.wind_end   || '';
      setWxS({ wind_speed: ws.split(' ')[0]||'', wind_dir: ws.split(' ').slice(1).join(' ')||'', baro: editEntry.baro_start||'', baro_trend: editEntry.baro_trend||'', visibility: editEntry.visibility||'' });
      setWxE({ wind_speed: we.split(' ')[0]||'', wind_dir: we.split(' ').slice(1).join(' ')||'', baro: editEntry.baro_end||'' });
    } else {
      setForm({
        date: new Date().toISOString().slice(0,10),
        trip_type: 'Day Sail', dest_type: 'Marina', captain: 'Frederick',
        engine_start: carryReadings?.engine_end ?? prevEntry?.engine_end ?? 2940,
        gen_start:    carryReadings?.gen_end    ?? prevEntry?.gen_end    ?? 1130,
        log_nm:       carryReadings?.log_nm     ?? undefined,
        water_used:   carryReadings?.water_used ?? undefined,
      });
      setWxS({}); setWxE({});
    }
    setFuelEst(null);
    setStep(0);
    setActive('import');
  }, [isOpen, editEntry, prevEntry, carryReadings]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const wxChange = (which, k, v) => which==='s' ? setWxS(w=>({...w,[k]:v})) : setWxE(w=>({...w,[k]:v}));

  // Fuel estimate
  useEffect(() => {
    const s=Number(form.engine_start), e=Number(form.engine_end), rpm=Number(form.avg_rpm);
    if(s&&e&&rpm&&e>s) setFuelEst(((e-s)*(YANMAR_CURVE[rpm]||1.19)).toFixed(1));
    else setFuelEst(null);
  }, [form.engine_start, form.engine_end, form.avg_rpm]);

  // Sail/motor
  const movHrs = parseFloat(form.moving_hrs)||null;
  const motHrs = movingMotorHrs(form);
  const sailHrs = (movHrs!=null && motHrs!=null) ? Math.max(movHrs - motHrs, 0) : null;
  const sailPct = sailingPct(form);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const entry = {
        ...form,
        wind_start: wxS.wind_speed ? `${wxS.wind_speed} ${wxS.wind_dir||''}`.trim() : null,
        wind_end:   wxE.wind_speed ? `${wxE.wind_speed} ${wxE.wind_dir||''}`.trim() : null,
        baro_start: wxS.baro||null, baro_trend: wxS.baro_trend||null,
        baro_end:   wxE.baro||null, visibility: wxS.visibility||null,
        fuel_est: fuelEst ? Number(fuelEst) : null,
        moving_hrs: movHrs, motor_hrs: motHrs,
        sail_hrs: sailHrs, sail_pct: sailPct,
      };
      // For a stay, fall back to the carried neighbour times if left blank.
      if (isStay(form.trip_type)) {
        if (!entry.arrive_time && carriedArrival)   entry.arrive_time = carriedArrival;
        if (!entry.depart_time && carriedDeparture) entry.depart_time = carriedDeparture;
      }
      await onSave(entry);
      onClose();
    } catch(err) {
      // onSave handles error display
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const showDestName = ['Anchor','Mooring','Marina','Other YC'].includes(form.dest_type);
  const destLbl = {Anchor:'Anchorage Name',Mooring:'Mooring / Field','Other YC':'Yacht Club Name',Marina:'Name'}[form.dest_type]||'Name';

  const stayNights = isStay(form.trip_type) ? nightsBetween(form.date, form.next_depart_date) : null;

  const carriedArrival   = stayNeighbours.prev?.arrive_time || '';
  const carriedDeparture = stayNeighbours.next?.depart_time || '';

  const allSections = [
    { id:'import', label:'Import', node:(<>
      <Sep first>Import</Sep>
      <div style={{ gridColumn:'1/-1' }}><GPXUpload existing={Array.isArray(form.track)&&form.track.length>1 ? { points: form.track.length, nm: form.nm } : null} onParsed={r => { set('date',r.date); set('depart_time',r.depart_time); set('arrive_time',r.arrive_time); set('nm',r.nm); set('moving_hrs',r.moving_hrs); set('max_sog',r.max_sog); set('avg_sog',r.avg_sog); set('trip_type',r.trip_type); set('start_lat',r.start_lat); set('start_lon',r.start_lon); set('end_lat',r.end_lat); set('end_lon',r.end_lon); if(r.track) set('track',r.track); if(r.title) set('title',r.title);
          if (r.start_lat != null && r.start_lon != null) reverseGeocode(r.start_lat, r.start_lon).then(n => { if (n) set('departure', n); });
          if (r.end_lat != null && r.end_lon != null) reverseGeocode(r.end_lat, r.end_lon).then(n => { if (n) set('destination', n); });
          // Sea state and weather at the two ends of the passage
          if (r.start_lat != null && r.date) fetchConditions(r.start_lat, r.start_lon, r.date, r.depart_time).then(c => {
            if (!c) return;
            setWxS(w => ({ ...w,
              wind_speed: w.wind_speed || c.wind_speed || '',
              wind_dir:   w.wind_dir   || c.wind_dir   || '',
              baro:       w.baro       || c.baro       || '',
              conditions: w.conditions || c.conditions || '',
              visibility: w.visibility || c.visibility || '',
              waves:      w.waves      || c.waves      || '',
            }));
            if (c.temp != null) set('temp', c.temp);
          });
          if (r.end_lat != null && r.date) fetchConditions(r.end_lat, r.end_lon, r.date, r.arrive_time).then(c => {
            if (!c) return;
            setWxE(w => ({ ...w,
              wind_speed: w.wind_speed || c.wind_speed || '',
              wind_dir:   w.wind_dir   || c.wind_dir   || '',
              baro:       w.baro       || c.baro       || '',
              conditions: w.conditions || c.conditions || '',
              waves:      w.waves      || c.waves      || '',
            }));
          }); }}/></div>
      <div style={{ gridColumn:'1/-1' }}>
        <Lbl sub="Upload 1 or more — each is parsed and merged">Orca Screenshots</Lbl>
        <MultiOrcaUpload existing={(form.avg_stw||form.avg_polar||form.stationary_hrs||form.avg_twd) ? { summary: [form.nm&&`${form.nm} nm`, form.avg_stw&&`STW ${form.avg_stw}`, form.avg_polar&&`polar ${form.avg_polar}%`].filter(Boolean).join(' · ') } : null} onParsed={r => {
          // Speeds & distance
          if (r.max_sog != null) set('max_sog', r.max_sog);
          if (r.avg_sog != null) set('avg_sog', r.avg_sog);
          if (r.distance_nm != null && !form.nm) set('nm', r.distance_nm);
          if (r.avg_stw != null) set('avg_stw', r.avg_stw);
          if (r.max_stw != null && Number(r.max_stw) < 50) set('max_stw', r.max_stw); // discard paddlewheel spikes
          // Wind
          if (r.max_tws != null) set('max_wind', r.max_tws);
          if (r.avg_tws != null) set('avg_wind', r.avg_tws);
          if (r.avg_twd != null) set('avg_twd', r.avg_twd);
          if (r.twd_stddev != null) set('twd_stddev', r.twd_stddev);
          // Current
          if (r.avg_current != null) set('avg_current', r.avg_current);
          if (r.max_current != null) set('max_current', r.max_current);
          if (r.current_dir != null) set('current_dir', r.current_dir);
          // Polar
          if (r.avg_polar != null) set('avg_polar', r.avg_polar);
          if (r.max_polar != null) set('max_polar', r.max_polar);
          // Batteries & environment
          if (r.batt_start != null) set('batt_start', r.batt_start);
          if (r.batt_end != null) set('batt_end', r.batt_end);
          if (r.batt_min != null) set('batt_min', r.batt_min);
          if (r.depth != null) set('depth', r.depth);
          if (r.water_temp != null) set('temp', r.water_temp);
          // Times — depart, travel (elapsed), navigation (moving), and derived arrival
          const travelH = orcaDurH(r.travel_time);
          const navH = orcaDurH(r.navigation_time);
          const statH = orcaDurH(r.stationary_time);
          if (r.starting_time) set('depart_time', orcaNormTime(r.starting_time));
          if (travelH != null) { set('total_time', orcaHHMM(travelH)); set('time_at_sea', travelH); }
          if (navH != null) set('moving_hrs', navH);
          if (statH != null) set('stationary_hrs', statH);
          if (r.starting_time && travelH != null) set('arrive_time', orcaAddTime(orcaNormTime(r.starting_time), travelH));
        }}/>
      </div>
    </>) },

    { id:'trip', label:'Trip', node:(<>
      <Sep>Trip</Sep>
      {!isStay(form.trip_type) && (
        <div><Lbl>Date</Lbl><input type="date" value={form.date||''} onChange={e=>set('date',e.target.value)}/></div>
      )}
      <div><Lbl>Trip Type</Lbl><select value={form.trip_type||'Day Sail'} onChange={e=>{ const t=e.target.value; set('trip_type',t); if (t==='Race') set('race',true); else if (form.trip_type==='Race') set('race',false); }}>{TRIP_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
      <div style={{ gridColumn:'1/-1' }}><Lbl>Trip Title</Lbl><input type="text" value={form.title||''} onChange={e=>set('title',e.target.value)} placeholder="Nice work commute."/></div>
      {isStay(form.trip_type) ? (
        <>
          <div style={{ gridColumn:'1/-1' }}><Lbl sub="One place: no passage logged">Location</Lbl><input type="text" value={form.destination||''} onChange={e=>{ set('destination',e.target.value); set('departure',e.target.value); }} placeholder={form.trip_type==='At Dock'?'Elliott Bay Marina':'Garden Bay'}/></div>
          <div><Lbl sub="Day the stay begins">Arrival Date</Lbl><input type="date" value={form.date||''} onChange={e=>{ const d=e.target.value; set('date',d); if(form.next_depart_date){ const n=nightsBetween(d,form.next_depart_date); if(n!=null) set('nights',String(n)); } }}/></div>
          <div><Lbl sub={carriedArrival ? 'Carried from last passage' : 'When you arrived'}>Arrival Time</Lbl><input type="time" value={form.arrive_time!=null?form.arrive_time:''} onChange={e=>set('arrive_time',e.target.value)} placeholder={carriedArrival}/></div>
          <div><Lbl sub="Day you leave">Departure Date</Lbl><input type="date" min={form.date||undefined} value={form.next_depart_date||''} onChange={e=>{ const dep=e.target.value; set('next_depart_date',dep); const n=nightsBetween(form.date,dep); if(n!=null) set('nights', String(n)); }}/></div>
          <div><Lbl sub={carriedDeparture ? 'Carried from next passage' : 'When you left'}>Departure Time</Lbl><input type="time" value={form.depart_time!=null?form.depart_time:''} onChange={e=>set('depart_time',e.target.value)} placeholder={carriedDeparture}/></div>
          {stayNights != null && (
            <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8, padding:'2px 2px 4px' }}>
              <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:600, color:'var(--navy)' }}>{stayNights}</span>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:'var(--slate)' }}>{stayNights === 1 ? 'night aboard' : 'nights aboard'}{stayNights === 0 ? ' · day visit' : ''}</span>
            </div>
          )}
          <div><Lbl sub="0 nights = day visit">Berth</Lbl><select value={form.dest_type||(form.trip_type==='At Dock'?'Marina':'Anchor')} onChange={e=>set('dest_type',e.target.value)}>{DEST_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        </>
      ) : (
        <>
          <div><Lbl>From</Lbl><input type="text" value={form.departure||''} onChange={e=>set('departure',e.target.value)} placeholder="Elliott Bay Marina"/></div>
          <div><Lbl sub="Optional midway stop">Stopover</Lbl><input type="text" value={form.stopover||''} onChange={e=>set('stopover',e.target.value)} placeholder="Blakely Harbor"/></div>
          <div><Lbl>To</Lbl><input type="text" value={form.destination||''} onChange={e=>set('destination',e.target.value)} placeholder="Friday Harbor"/></div>
          <div><Lbl>Depart Time</Lbl><input type="time" value={form.depart_time||''} onChange={e=>set('depart_time',e.target.value)}/></div>
          <div><Lbl>Arrive Time</Lbl><input type="time" value={form.arrive_time||''} onChange={e=>set('arrive_time',e.target.value)}/></div>
          <div><Lbl>Destination Type</Lbl><select value={form.dest_type||'Marina'} onChange={e=>set('dest_type',e.target.value)}>{DEST_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
          {showDestName&&<div><Lbl>{destLbl}</Lbl><input type="text" value={form.dest_name||''} onChange={e=>set('dest_name',e.target.value)}/></div>}
          <div><Lbl sub="Auto from GPX">Distance (nm)</Lbl><input type="number" step=".1" value={form.nm||''} onChange={e=>set('nm',e.target.value)} placeholder="14.6" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
        </>
      )}
      {!isStay(form.trip_type) && (
        <div style={{ gridColumn:'1/-1' }}>
          <div style={{ background:'var(--s2)', borderRadius:8, padding:'10px 14px' }}>
            <Check label="⚓ Staying overnight" checked={form.overnight} onChange={v=>set('overnight',v)}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:8, marginTop:6 }}>
                <div><Lbl>Nights</Lbl><select value={form.nights||'1'} onChange={e=>set('nights',e.target.value)}>{['1','2','3','4','5','6','7'].map(n=><option key={n}>{n}</option>)}</select></div>
                <div><Lbl>Next leg departs</Lbl><input type="date" value={form.next_depart_date||''} onChange={e=>set('next_depart_date',e.target.value)}/></div>
                <div style={{ gridColumn:'1/-1' }}><Lbl>Stay notes</Lbl><input type="text" value={form.overnight_notes||''} onChange={e=>set('overnight_notes',e.target.value)} placeholder="Stern-to on the dock…"/></div>
              </div>
            </Check>
          </div>
        </div>
      )}
    </>) },

    { id:'time', label:'Time', node:(<>
      <Sep>Time</Sep>
      <div><Lbl sub="SOG > 0.2 kts · auto from GPX">Moving Time (hrs)</Lbl><input type="number" step=".01" value={form.moving_hrs||''} onChange={e=>set('moving_hrs',e.target.value)} placeholder="5.75" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
      <div><Lbl>Total Time</Lbl><input type="text" value={form.total_time||''} onChange={e=>set('total_time',e.target.value)} placeholder="06:30 hh:mm"/></div>

      {movHrs&&(
        <div style={{ gridColumn:'1/-1', background:'var(--s2)', borderRadius:8, padding:'11px 14px' }}>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.12em', color:'var(--gold)', marginBottom:8 }}>Sail / Motor Ratio</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, textAlign:'center', marginBottom:8 }}>
            {[['Moving',movHrs.toFixed(1)+'h','var(--navy)'],['⛵ Sail',(sailHrs||0).toFixed(1)+'h','var(--green)'],['⚙ Motor',(motHrs||0).toFixed(1)+'h','var(--clay)']].map(([l,v,c])=>(
              <div key={l}><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:600, color:c }}>{v}</div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, textTransform:'uppercase', color:'var(--slate)', marginTop:2 }}>{l}</div></div>
            ))}
          </div>
          <div style={{ height:7, background:'var(--border)', borderRadius:3, overflow:'hidden' }}><div style={{ height:'100%', width:`${sailPct||0}%`, background:'linear-gradient(90deg,var(--green),#2d9e5f)', borderRadius:3 }}/></div>
          {sailPct!==null&&<div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:4, textAlign:'center' }}>{sailPct}% sailing · {100-sailPct}% motoring</div>}
        </div>
      )}
    </>) },

    { id:'engine', label:'Engine & Gen', node:(<>
      <Sep>Engine &amp; Generator</Sep>
      <div style={{ gridColumn:'1/-1', background:'rgba(184,134,11,.08)', border:'1px solid rgba(184,134,11,.22)', borderRadius:7, padding:'7px 12px', fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--ink-s)', display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ color:'var(--gold)' }}>⚡</span> Start values carried from previous entry — tap to override
      </div>
      <HrsRow label="Engine Hours — Yanmar 4JH3TE" sKey="engine_start" eKey="engine_end" sVal={form.engine_start} eVal={form.engine_end} onChange={set}/>
      <HrsRow label="Generator Hours — Northern Lights 6KW" sKey="gen_start" eKey="gen_end" sVal={form.gen_start} eVal={form.gen_end} onChange={set}/>
      <div><Lbl>Avg RPM (owner)</Lbl><select value={form.avg_rpm||''} onChange={e=>set('avg_rpm',e.target.value)}><option value="">— select RPM —</option>{Object.entries(YANMAR_CURVE).map(([rpm,gph])=><option key={rpm} value={rpm}>{rpm} RPM → {gph} gal/hr</option>)}</select></div>
      <div><Lbl>Fuel Estimate</Lbl><div style={{ background:fuelEst?'var(--navy)':'var(--s2)', borderRadius:7, padding:'9px 14px', textAlign:'center' }}><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:600, color:fuelEst?'var(--gold-lt)':'var(--slate)' }}>{fuelEst?`${fuelEst} gal`:'—'}</div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:fuelEst?'rgba(255,255,255,.5)':'var(--slate)', marginTop:2 }}>YANMAR 4JH3TE</div></div></div>
    </>) },

    { id:'tanks', label:'Tanks & Batt', node:(<>
      <Sep>Tanks, Battery &amp; Water</Sep>
      <TankRow label="Fuel Tank 1 — Main (90 gal)" sKey="fuel_t1_start" eKey="fuel_t1_end" sVal={form.fuel_t1_start} eVal={form.fuel_t1_end} onChange={set}/>
      <TankRow label="Fuel Tank 2 — Aux (38 gal)"  sKey="fuel_t2_start" eKey="fuel_t2_end" sVal={form.fuel_t2_start} eVal={form.fuel_t2_end} onChange={set}/>
      <div><Lbl sub="Volts at departure">Battery Start (V)</Lbl><input type="number" step=".1" value={form.batt_start||''} onChange={e=>set('batt_start',e.target.value)} placeholder="13.2" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
      <div><Lbl sub="Volts at arrival">Battery End (V)</Lbl><input type="number" step=".1" value={form.batt_end||''} onChange={e=>set('batt_end',e.target.value)} placeholder="12.8" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
      <div><Lbl sub="Gauge litres used · 0 = full (445 L)">Fresh Water Gauge (L)</Lbl><input type="number" value={form.water_topoff?'0':(form.water_used!=null?form.water_used:'')} disabled={!!form.water_topoff} onChange={e=>set('water_used',e.target.value)} placeholder="19" style={{ fontFamily:"'IBM Plex Mono',monospace", opacity:form.water_topoff?.5:1 }}/></div>
      <div><Lbl sub="Tanks filled this entry">Topped Off?</Lbl><select value={form.water_topoff?'Y':''} onChange={e=>{ const y=e.target.value==='Y'; set('water_topoff',y); if(y) set('water_used','0'); }}><option value="">No</option><option value="Y">Yes — tanks full</option></select></div>
    </>) },

    { id:'checks', label:'Checks', node:(<>
      <Sep>Engine Checks</Sep>
      <div style={{ gridColumn:'1/-1', background:'var(--s2)', borderRadius:8, padding:'10px 14px' }}>
        <Check label="Oil level checked" checked={form.chk_oil} onChange={v=>set('chk_oil',v)}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:8, marginTop:6 }}>
            <div><Lbl>Qt added</Lbl><input type="number" step=".25" value={form.oil_qty||''} onChange={e=>set('oil_qty',e.target.value)} placeholder="0.5 qt" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
            <div><Lbl>Eng hrs at check</Lbl><input type="number" step=".1" value={form.oil_eng_hrs||form.engine_end||''} onChange={e=>set('oil_eng_hrs',e.target.value)} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
          </div>
        </Check>
        <Check label="Raw water strainer cleaned" checked={form.chk_strainer} onChange={v=>set('chk_strainer',v)}/>
        <Check label="Bilge checked" checked={form.chk_bilge} onChange={v=>set('chk_bilge',v)}/>
        <Check label="Fuel added" checked={form.chk_fuel_added} onChange={v=>set('chk_fuel_added',v)}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:8, marginTop:6 }}>
            <div><Lbl>Gallons</Lbl><input type="number" step=".1" value={form.fuel_gal||''} onChange={e=>set('fuel_gal',e.target.value)} placeholder="22.0" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
            <div><Lbl>$/gal</Lbl><input type="number" step=".01" value={form.fuel_ppg||''} onChange={e=>set('fuel_ppg',e.target.value)} placeholder="5.49" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
            <div><Lbl>Engine hrs at fill</Lbl><input type="number" step=".1" value={form.fuel_eng_hrs||form.engine_end||''} onChange={e=>set('fuel_eng_hrs',e.target.value)} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
            <div><Lbl>Location</Lbl><input type="text" value={form.fuel_location||''} onChange={e=>set('fuel_location',e.target.value)} placeholder="EBM"/></div>
            {form.fuel_gal&&form.fuel_ppg&&<div style={{ gridColumn:'1/-1', background:'rgba(11,22,38,.06)', borderRadius:6, padding:'7px 12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)' }}>Total</span><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:15, fontWeight:700, color:'var(--navy)' }}>${(Number(form.fuel_gal)*Number(form.fuel_ppg)).toFixed(2)}</span></div>}
          </div>
        </Check>
      </div>
    </>) },

    { id:'sails', label:'Sail Plan', node:(<>
      <Sep>Sail Plan</Sep>
      {[['Main','sail_main',['Full main','1st reef','2nd reef','Furled']],['Headsail','sail_headsail',['Genoa (full)','Genoa (partial)','Furled']],['Spinnaker','sail_spin',['Asymmetric up','Furled / stowed']],['Mode','sail_mode',['Sail','Motor','Motor-sail','Anchored']]].map(([lbl,k,opts])=>(
        <div key={k}><Lbl>{lbl}</Lbl><select value={form[k]||''} onChange={e=>set(k,e.target.value)}><option value="">—</option>{opts.map(o=><option key={o}>{o}</option>)}</select></div>
      ))}
    </>) },

    { id:'perf', label:'Performance', node:(<>
      <Sep>Performance</Sep>
      <div><Lbl sub="From Orca or GPX">Max SOG (kts)</Lbl><input type="number" step=".1" value={form.max_sog||''} onChange={e=>set('max_sog',e.target.value)} placeholder="8.4" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
      <div><Lbl>Avg SOG (kts)</Lbl><input type="number" step=".1" value={form.avg_sog||''} onChange={e=>set('avg_sog',e.target.value)} placeholder="5.2" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
      <div><Lbl>Max Wind (kts)</Lbl><input type="number" step=".1" value={form.max_wind||''} onChange={e=>set('max_wind',e.target.value)} placeholder="22" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
    </>) },

    { id:'weather', label:'Weather', node:(<>
      <Sep>Logbook weather</Sep>
      <div><Lbl sub="Free text, e.g. 5-15 N">Wind</Lbl><input type="text" value={form.wind||''} onChange={e=>set('wind',e.target.value)} placeholder="5-15 N"/></div>
      <div><Lbl>Temp (°F)</Lbl><input type="number" value={form.temp!=null?form.temp:''} onChange={e=>set('temp',e.target.value)} placeholder="65"/></div>
      <div><Lbl>Visibility</Lbl><input type="text" value={form.visibility||''} onChange={e=>set('visibility',e.target.value)} placeholder="Clear / Fog"/></div>
      <div><Lbl sub="O overnight · D daytime">Anchored?</Lbl><select value={form.anchoring||''} onChange={e=>set('anchoring',e.target.value)}><option value="">No</option><option value="O">Overnight</option><option value="D">Daytime</option></select></div>
      {[['s','Detailed — Departure',wxS,setWxS],['e','Detailed — Arrival',wxE,setWxE]].map(([which,label,wx,setWx])=>(
        <React.Fragment key={which}>
          <Sep>{label}</Sep>
          <div><Lbl>Wind Speed (kts)</Lbl><input type="number" value={wx.wind_speed||''} onChange={e=>setWx(w=>({...w,wind_speed:e.target.value}))} placeholder="14"/></div>
          <div><Lbl>Wind Direction</Lbl><input type="text" value={wx.wind_dir||''} onChange={e=>setWx(w=>({...w,wind_dir:e.target.value}))} placeholder="SW · 225°"/></div>
          <div><Lbl>Barometer (mb)</Lbl><input type="number" value={wx.baro||''} onChange={e=>setWx(w=>({...w,baro:e.target.value}))} placeholder="1018"/></div>
          <div><Lbl>Trend</Lbl><select value={wx.baro_trend||''} onChange={e=>setWx(w=>({...w,baro_trend:e.target.value}))}><option value="">—</option>{BARO_TREND.map(t=><option key={t}>{t}</option>)}</select></div>
          <div style={{ gridColumn:'1/-1' }}><Lbl>Conditions</Lbl><input type="text" value={wx.conditions||''} onChange={e=>setWx(w=>({...w,conditions:e.target.value}))} placeholder="Sunny, building SW swell"/></div>
          <div><Lbl>Visibility</Lbl><select value={wx.visibility||''} onChange={e=>setWx(w=>({...w,visibility:e.target.value}))}><option value="">—</option>{VISIBILITY_OPTS.map(v=><option key={v}>{v}</option>)}</select></div>
          <div><Lbl>Waves</Lbl><input type="text" value={wx.waves||''} onChange={e=>setWx(w=>({...w,waves:e.target.value}))} placeholder="2ft SW chop"/></div>
        </React.Fragment>
      ))}
    </>) },

    { id:'crew', label:'Crew', node:(<>
      <Sep>Crew</Sep>
      <div><Lbl>Captain</Lbl><input type="text" value={form.captain||'Frederick'} onChange={e=>set('captain',e.target.value)}/></div>
      <div><Lbl>Crew</Lbl><input type="text" value={form.crew||''} onChange={e=>set('crew',e.target.value)} placeholder="Jules, Alec"/></div>
      <div><Lbl>Solo?</Lbl><select value={form.solo||''} onChange={e=>set('solo',e.target.value)}><option value="">No</option><option value="Y">Yes</option></select></div>
      <div><Lbl>Race?</Lbl><select value={form.race?'Y':''} onChange={e=>{ const y=e.target.value==='Y'; set('race',y); if (y) set('trip_type','Race'); }}><option value="">No</option><option value="Y">Yes</option></select></div>
    </>) },

    { id:'rating', label:'Rating', node:(<>
      <Sep>Rating</Sep>
      <div><Lbl>Difficulty</Lbl><select value={form.difficulty||''} onChange={e=>set('difficulty',e.target.value)}><option value="">—</option>{[['E','Easy'],['M','Medium'],['H','Hard'],['VH','Very Hard']].map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
      <div><Lbl>Stars</Lbl><div style={{ display:'flex', gap:6, paddingTop:7 }}>{[1,2,3,4,5].map(n=><span key={n} onClick={()=>set('stars',n)} style={{ fontSize:22, cursor:'pointer', opacity:(form.stars||0)>=n?1:.25, transition:'opacity .15s' }}>⭐</span>)}</div></div>
    </>) },

    { id:'notes', label:'Notes', node:(<>
      <Sep>Notes</Sep>
      <div style={{ gridColumn:'1/-1' }}><Lbl>Notes</Lbl><textarea value={form.comments||''} onChange={e=>set('comments',e.target.value)} placeholder="Departed on a building flood…"/></div>
    </>) },

    { id:'photos', label:'Photos', node:(<>
      <Sep>Photos</Sep>
      <div style={{ gridColumn:'1/-1' }}>
        <PhotoUpload
          photos={form.photos}
          coverUrl={form.photo_url}
          date={form.date}
          onChange={(next, cover) => { set('photos', next); set('photo_url', cover); }}
        />
      </div>
    </>) },
  ];

  // A stay (at anchor / at dock) has no passage, so drop the underway-only
  // tabs. Engine, generator, water, batteries, checks, weather stay.
  const STAY_HIDE = ['import', 'time', 'sails', 'perf'];
  const sections = isStay(form.trip_type)
    ? allSections.filter(sec => !STAY_HIDE.includes(sec.id))
    : allSections;

  // Keep the step/active tab valid if the section list shrinks (sail → stay).

  const goTo = (id) => {
    setActive(id);
    const el = secRefs.current[id], c = scrollRef.current;
    if (el && c) c.scrollTo({ top: Math.max(el.offsetTop - 8, 0), behavior:'smooth' });
  };
  const onScrollSpy = () => {
    const c = scrollRef.current;
    if (!c) return;
    const y = c.scrollTop + 100;
    let cur = sections[0].id;
    for (const s of sections) { const el = secRefs.current[s.id]; if (el && el.offsetTop <= y) cur = s.id; }
    setActive(cur);
  };

  const railGrid = 'repeat(auto-fit, minmax(185px, 1fr))';
  const btnOutline = { fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, padding:'10px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--slate)', cursor:'pointer' };
  const btnPrimary = { fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:700, padding:'10px 22px', borderRadius:7, border:'none', background:'linear-gradient(135deg,var(--navy),#1d3e63)', color:'var(--gold-lt)', cursor:'pointer', opacity:saving?.7:1 };

  // ── MOBILE — stepped, one section at a time ──
  if (mobile) {
    const last = sections.length - 1;
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:300, display:'flex', alignItems:'flex-end', justifyContent:'center' }} onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div style={{ background:'var(--surface)', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:640, height:'94vh', display:'flex', flexDirection:'column', boxShadow:'var(--sh-lg)', overflow:'hidden' }}>
          <style>{`.pmf-mob input,.pmf-mob select,.pmf-mob textarea{font-size:16px;}`}</style>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'var(--slate)', cursor:'pointer' }}>✕</button>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--navy)' }}>{editEntry ? 'Edit entry' : 'New entry'}</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:600, color:'var(--gold)' }}>{step+1} / {sections.length}</div>
          </div>
          <div style={{ display:'flex', gap:6, padding:'10px 16px', overflowX:'auto', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            {sections.map((s,i)=>(
              <button key={s.id} onClick={()=>setStep(i)} style={{ flex:'0 0 auto', padding:'6px 0', marginRight:16, background:'none', border:'none', cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color: step===i?'var(--navy)':'var(--slate)', borderBottom: step===i?'2px solid var(--gold)':'2px solid transparent', whiteSpace:'nowrap' }}>{s.label}</button>
            ))}
          </div>
          <div className="pmf-mob" style={{ flex:1, overflowY:'auto', padding:'14px 16px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10, alignContent:'start' }}>
            {sections[step].node}
          </div>
          <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:10, flexShrink:0, paddingBottom:'max(12px, env(safe-area-inset-bottom))' }}>
            {step>0 && <button onClick={()=>setStep(s=>s-1)} style={{ ...btnOutline, flex:'0 0 auto' }}>Back</button>}
            {step < last
              ? <button onClick={()=>setStep(s=>s+1)} style={{ ...btnPrimary, flex:1 }}>Next · {sections[step+1].label}</button>
              : <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, flex:1 }}>{saving ? 'Saving…' : editEntry ? 'Update entry' : 'Save entry'}</button>}
          </div>
        </div>
      </div>
    );
  }

  // ── DESKTOP — section rail + multi-column content ──
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'var(--surface)', borderRadius:16, width:'100%', maxWidth:980, height:'88vh', display:'flex', flexDirection:'column', boxShadow:'var(--sh-lg)', overflow:'hidden' }}>
        <div style={{ padding:'15px 22px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:'var(--navy)' }}>{editEntry ? 'Edit log entry' : 'New log entry'}</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', marginTop:2 }}>{form.trip_type||'Day Sail'}{form.date?` · ${form.date}`:''}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, color:'var(--slate)', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'190px 1fr', flex:1, minHeight:0 }}>
          <aside style={{ background:'var(--bg)', borderRight:'1px solid var(--border)', padding:'14px 10px', overflowY:'auto' }}>
            {sections.map(s=>(
              <button key={s.id} onClick={()=>goTo(s.id)} style={{ display:'block', width:'100%', textAlign:'left', padding:'9px 12px', marginBottom:2, borderRadius:8, border:'none', cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', background: active===s.id?'var(--navy)':'transparent', color: active===s.id?'var(--gold-lt)':'var(--slate)' }}>{s.label}</button>
            ))}
          </aside>
          <div ref={scrollRef} onScroll={onScrollSpy} style={{ overflowY:'auto', padding:'16px 22px', position:'relative' }}>
            {sections.map(s=>(
              <div key={s.id} ref={el=>{ secRefs.current[s.id]=el; }} style={{ display:'grid', gridTemplateColumns:railGrid, gap:10, alignContent:'start', paddingBottom:16 }}>
                {s.node}
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding:'12px 22px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>
          <button onClick={onClose} style={btnOutline}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : editEntry ? 'Update entry' : 'Save entry'}</button>
        </div>
      </div>
    </div>
  );
}
